import { Inject, Injectable } from "@angular/core";
import { loadActiveRole } from "../../../global-states/session/load-active-role";
import { IQuickAccessResourcesResolver } from "./i-quick-access-resources.resolver";
import {
  sessionStateServiceInjectionToken
} from "../../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../../global-states/session/i-session-state.service";
import { cloudRestClientInjectionToken } from "../../../rest-clients/cloud/cloud-rest-client-injection-token";
import { ICloudRestClient } from "../../../rest-clients/cloud/i-cloud-rest.client";
import { IGetCloudPageForm } from "../../../rest-clients/cloud/forms/i-get-cloud-page-form";
import { ICloud } from "../../../models/i-cloud";
import { ICompany } from "../../../models/i-company";
import { IPage } from "@zaeper/communication-lib";
import { IGetGroupPageForm } from "../../../rest-clients/group/forms/i-get-group-page-form";
import { IGroup } from "../../../models/i-group";
import { groupRestClientInjectionToken } from "../../../rest-clients/group/group-rest-client-injection-token";
import { IGroupRestClient } from "../../../rest-clients/group/i-group-rest.client";
import { IGetReceiverPageForm } from "../../../rest-clients/receiver/forms/i-get-receiver-page-form";
import { receiverRestClientInjectionToken } from "../../../rest-clients/receiver/receiver-rest-client-injection-token";
import { IReceiverRestClient } from "../../../rest-clients/receiver/i-receiver-rest.client";
import { IReceiver } from "../../../models/i-receiver";
import { companyRestClientInjectionToken } from "../../../rest-clients/company/company-rest-client-injection-token";
import { ICompanyRestClient } from "../../../rest-clients/company/i-company-rest.client";
import { licenseRestClientInjectionToken } from "../../../rest-clients/license/license-rest-client-injection-token";
import { ILicenseRestClient } from "../../../rest-clients/license/i-license-rest.client";

@Injectable()
export class QuickAccessResourcesResolver implements IQuickAccessResourcesResolver {
  constructor(@Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
              @Inject(cloudRestClientInjectionToken) private readonly _cloudRestClient: ICloudRestClient,
              @Inject(groupRestClientInjectionToken) private readonly _groupRestClient: IGroupRestClient,
              @Inject(receiverRestClientInjectionToken) private readonly _receiverRestClient: IReceiverRestClient,
              @Inject(companyRestClientInjectionToken) private readonly _companyRestClient: ICompanyRestClient,
              @Inject(licenseRestClientInjectionToken) private readonly _licenseRestClient: ILicenseRestClient) {
  }

  public async getQuickAccessResources(): Promise<void> {
    await this._initializeActiveCompany();

    if (!this._sessionStateService.getActiveCompany()) {
      return;
    }

    await this._getQuickAccessClouds();
    await this._getQuickAccessGroups();
    return await this._getQuickAccessReceivers();
  };

  public async reloadForActiveCompany(): Promise<void> {
    this._sessionStateService.setQuickAccessClouds([]);
    this._sessionStateService.setQuickAccessGroups([]);
    this._sessionStateService.setQuickAccessReceivers([]);
    this._sessionStateService.setActiveReceiver(null);
    this._sessionStateService.setActiveGroup(null);
    this._sessionStateService.setActiveCloud(null);

    if (!this._sessionStateService.getActiveCompany()) {
      return;
    }

    await this._getQuickAccessClouds();
    await this._getQuickAccessGroups();
    await this._getQuickAccessReceivers().catch(() => undefined);
  }

  private async _initializeActiveCompany(): Promise<void> {
    try {
      const companyPage: IPage<ICompany> = await this._companyRestClient.getCompanyPage({ pageNumber: 0, pageSize: 100 });

      if (companyPage.records.length > 0) {
        this._sessionStateService.setActiveCompany(companyPage.records[0]);
        await loadActiveRole(this._sessionStateService, this._companyRestClient, companyPage.records[0].id);

        try {
          const license = await this._licenseRestClient.getLicenseByCompany(companyPage.records[0].id);
          this._sessionStateService.setActiveLicense(license);
        } catch {
          // No license found — free tier may not be assigned yet
        }
      } else {
        const pendingRequest = await this._companyRestClient.getMyPendingJoinRequest();
        this._sessionStateService.setPendingJoinRequest(pendingRequest);
      }
    } catch {
      // Company service unavailable — continue without company context
    }
  }

  private async _getQuickAccessClouds(): Promise<void> {
    const getCloudPageForm: IGetCloudPageForm = {
      pageNumber: 0,
      pageSize: 10,
      companyId: this._sessionStateService.getActiveCompany()?.id
    }

    const cloudPage: IPage<ICloud> = await this._cloudRestClient.getCloudPage(getCloudPageForm);
    this._sessionStateService.setQuickAccessClouds(cloudPage.records);
    if (cloudPage.records.length > 0) {
      this._sessionStateService.setActiveCloud(cloudPage.records[0]);
    }
  }

  private async _getQuickAccessGroups(): Promise<void> {
    const activeCloud: ICloud | null = this._sessionStateService.getActiveCloud();

    if (activeCloud == null || activeCloud.id === undefined) {
      return;
    }

    const cloudId: string = activeCloud.id;

    const getGroupPageForm: IGetGroupPageForm = {
      cloudId,
      pageNumber: 0,
      pageSize: 10
    }

    const groupPage: IPage<IGroup> = await this._groupRestClient.getGroupPage(getGroupPageForm);
    this._sessionStateService.setQuickAccessGroups(groupPage.records);
    if (groupPage.records.length > 0) {
      this._sessionStateService.setActiveGroup(groupPage.records[0]);
    }
  }

  private async _getQuickAccessReceivers(): Promise<void> {
    const activeCloud: ICloud | null = this._sessionStateService.getActiveCloud();
    const activeGroup: IGroup | null = this._sessionStateService.getActiveGroup();

    if (activeCloud == null || activeCloud.id === undefined || activeGroup == null || activeGroup.id === undefined) {
      return Promise.reject();
    }

    const cloudId: string = activeCloud.id;
    const groupId: string = activeGroup.id;

    const getReceiverPageForm: IGetReceiverPageForm = {
      cloudId,
      groupId,
      pageNumber: 0,
      pageSize: 10
    }

    const receiverPage: IPage<IReceiver> = await this._receiverRestClient.getReceiverPage(getReceiverPageForm);
    if (receiverPage.records.length > 0) {
      this._sessionStateService.setActiveReceiver(receiverPage.records[0]);
      this._sessionStateService.setQuickAccessReceivers(receiverPage.records);
      return Promise.resolve();
    }
  }
}
